import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../styles/midnight-lab.css';

const teamMembers = [
    {
      id: 1,
      name: 'Alex Johnson',
      role: 'Captain',
      division: 'FTC',
      image: '/assets/IMG_3291.jpeg',
      bio: 'Lead engineer and team captain'
    },
    {
      id: 2,
      name: 'Sam Chen',
      role: 'Mechanical Lead',
      division: 'FTC',
      image: '/assets/IMG_6006.jpeg',
      bio: 'Specializes in drivetrain and mechanisms'
    },
    {
      id: 3,
      name: 'Jordan Martinez',
      role: 'Programming Lead',
      division: 'FTC',
      image: '/assets/IMG_8549.jpg',
      bio: 'Autonomous routines and vision processing'
    },
    {
      id: 4,
      name: 'Taylor Kim',
      role: 'Strategy Lead',
      division: 'FTC',
      image: '/assets/IMG_8550.jpg',
      bio: 'Competition strategy and scouting'
    },
    {
      id: 5,
      name: 'Morgan Lee',
      role: 'Outreach Lead',
      division: 'FTC',
      image: '/assets/IMG_8551.jpg',
      bio: 'Community engagement and mentoring'
    },
    {
      id: 6,
      name: 'Casey Brown',
      role: 'Electrical Lead',
      division: 'FTC',
      image: '/assets/IMG_3291.jpeg',
      bio: 'Wiring and electrical systems'
    },
    {
      id: 7,
      name: 'Riley Davis',
      role: 'Design Lead',
      division: 'FTC',
      image: '/assets/IMG_6006.jpeg',
      bio: 'CAD design and prototyping'
    },
    {
      id: 8,
      name: 'Quinn Wilson',
      role: 'Member',
      division: 'FTC',
      image: '/assets/IMG_8549.jpg',
      bio: 'Mechanical and programming support'
    },
    {
      id: 9,
      name: 'Avery Garcia',
      role: 'Member',
      division: 'FTC',
      image: '/assets/IMG_8550.jpg',
      bio: 'Build and competition support'
    },
    {
      id: 10,
      name: 'Blake Thompson',
      role: 'Member',
      division: 'FTC',
      image: '/assets/IMG_8551.jpg',
      bio: 'Outreach and documentation'
    },
    {
      id: 11,
      name: 'Cameron White',
      role: 'Member',
      division: 'FTC',
      image: '/assets/IMG_3291.jpeg',
      bio: 'Programming and testing'
    },
    {
      id: 12,
      name: 'Dakota Anderson',
      role: 'Member',
      division: 'FTC',
      image: '/assets/IMG_6006.jpeg',
      bio: 'Mechanical assembly and maintenance'
    }
];

function TeamPage() {
  const [selectedRole, setSelectedRole] = useState('all');
  const [visible, setVisible] = useState({});
  const [imageVisible, setImageVisible] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const sectionRefs = useRef({});
  const imageRefs = useRef({});

  const roles = [
    { value: 'all', label: 'All Roles' },
    { value: 'Captain', label: 'Captain' },
    { value: 'Mechanical Lead', label: 'Mechanical' },
    { value: 'Programming Lead', label: 'Programming' },
    { value: 'Strategy Lead', label: 'Strategy' },
    { value: 'Outreach Lead', label: 'Outreach' },
    { value: 'Electrical Lead', label: 'Electrical' },
    { value: 'Design Lead', label: 'Design' },
    { value: 'Member', label: 'Members' }
  ];

  const filteredMembers = useMemo(() => {
    return teamMembers.filter(member => {
      return selectedRole === 'all' || member.role === selectedRole;
    });
  }, [selectedRole]);

  useEffect(() => {
    const observers = Object.keys(sectionRefs.current).map((key) => {
      return new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.1 }
      );
    });

    Object.keys(sectionRefs.current).forEach((key, index) => {
      if (sectionRefs.current[key]) {
        observers[index].observe(sectionRefs.current[key]);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  // Staggered animation for team member cards
  useEffect(() => {
    setImageVisible({}); // Reset visibility when filters change
    const filtered = filteredMembers;
    const observers = filtered.map((member, index) => {
      return new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setImageVisible((prev) => ({ ...prev, [member.id]: true }));
            }, index * 50);
          }
        },
        { threshold: 0.1 }
      );
    });

    filtered.forEach((member, index) => {
      const ref = imageRefs.current[member.id];
      if (ref && observers[index]) {
        observers[index].observe(ref);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [filteredMembers]);

  return (
    <div style={{ backgroundColor: 'var(--color-bg-primary)', paddingTop: '100px' }}>
      {/* Hero Section */}
      <section
        ref={(el) => (sectionRefs.current['hero'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          textAlign: 'center',
          opacity: visible.hero ? 1 : 0,
          transform: visible.hero ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <h1 style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(3rem, 6vw, 5rem)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-6)',
          letterSpacing: '-0.03em'
        }}>
          Our Team
        </h1>
        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
          color: 'var(--color-text-secondary)',
          lineHeight: 1.7,
          maxWidth: '700px',
          margin: '0 auto'
        }}>
          Meet the passionate students building the future of robotics
        </p>
      </section>

      {/* Role Filter */}
      <section
        ref={(el) => (sectionRefs.current['filters'] = el)}
        style={{
          padding: 'var(--space-16) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.filters ? 1 : 0,
          transform: visible.filters ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          borderBottom: '1px solid rgba(0, 16, 17, 0.1)',
          justifyContent: 'center',
          flexWrap: 'wrap',
          paddingBottom: 'var(--space-4)'
        }}>
          {roles.map((role) => (
            <button
              key={role.value}
              onClick={() => setSelectedRole(role.value)}
              style={{
                padding: 'var(--space-3) var(--space-6)',
                backgroundColor: selectedRole === role.value ? 'var(--color-text-primary)' : 'transparent',
                color: selectedRole === role.value ? 'var(--color-text-light)' : 'var(--color-text-primary)',
                border: 'none',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all var(--transition-base)',
                textTransform: 'none'
              }}
              onMouseEnter={(e) => {
                if (selectedRole !== role.value) {
                  e.target.style.backgroundColor = 'var(--color-bg-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedRole !== role.value) {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              {role.label}
            </button>
          ))}
        </div>
      </section>

      {/* Team Grid */}
      <section
        ref={(el) => (sectionRefs.current['team'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.team ? 1 : 0,
          transform: visible.team ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 'var(--space-8)'
        }}>
          {filteredMembers.map((member) => (
            <div
              key={member.id}
              ref={(el) => (imageRefs.current[member.id] = el)}
              onClick={() => setSelectedImage(member)}
              style={{
                cursor: 'pointer',
                opacity: imageVisible[member.id] ? 1 : 0,
                transform: imageVisible[member.id]
                  ? 'translateY(0) scale(1)'
                  : 'translateY(20px) scale(0.98)',
                transition: 'all 0.6s ease-out',
                animation: imageVisible[member.id]
                  ? 'fadeInUp 0.6s ease-out forwards'
                  : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = imageVisible[member.id]
                  ? 'translateY(0) scale(1)'
                  : 'translateY(20px) scale(0.98)';
              }}
            >
              <div style={{
                aspectRatio: '3/4',
                backgroundImage: `url(${member.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                marginBottom: 'var(--space-4)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(to top, rgba(0, 16, 17, 0.8) 0%, transparent 100%)',
                  padding: 'var(--space-4)',
                  transform: 'translateY(100%)',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(100%)';
                }}
                >
                  <div style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-light)',
                    lineHeight: 1.6
                  }}>
                    {member.bio}
                  </div>
                </div>
              </div>
              <div>
                <h3 style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-xl)',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  marginBottom: 'var(--space-1)'
                }}>
                  {member.name}
                </h3>
                <div style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-frozen-lake)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 'var(--space-1)'
                }}>
                  {member.role}
                </div>
                <div style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {member.division}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div
          onClick={() => setSelectedImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 16, 17, 0.95)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-4)',
            cursor: 'pointer'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '800px',
              width: '100%',
              backgroundColor: 'var(--color-bg-primary)',
              padding: 'var(--space-8)',
              cursor: 'default'
            }}
          >
            <button
              onClick={() => setSelectedImage(null)}
              style={{
                position: 'absolute',
                top: 'var(--space-4)',
                right: 'var(--space-4)',
                width: '40px',
                height: '40px',
                backgroundColor: 'rgba(255, 255, 252, 0.9)',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div style={{
              aspectRatio: '3/4',
              backgroundImage: `url(${selectedImage.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              marginBottom: 'var(--space-6)'
            }} />
            <h2 style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-3xl)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              marginBottom: 'var(--space-2)'
            }}>
              {selectedImage.name}
            </h2>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-lg)',
              color: 'var(--color-frozen-lake)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 'var(--space-4)'
            }}>
              {selectedImage.role} • {selectedImage.division}
            </div>
            <p style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-base)',
              color: 'var(--color-text-secondary)',
              lineHeight: 1.7
            }}>
              {selectedImage.bio}
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default TeamPage;

